import { ApplicationConfig, inject, provideAppInitializer, provideBrowserGlobalErrorListeners, provideZonelessChangeDetection } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { provideFirebaseApp, initializeApp } from '@angular/fire/app';
import { provideAuth, getAuth } from '@angular/fire/auth';
import { UserStateService } from './core/services/user-state/user-state';
import { LoginService } from './core/services/login/login';
import { UserService } from './core/services/user/user';
import { UserInteractionService } from './core/services/user-interaction/user-interaction';
import { AnalyticsService } from './core/services/analytics/analytics';
import { firstValueFrom } from 'rxjs';

import { routes } from './app.routes';
import { environment } from '../environments/environment';

/**
 * Initializer function that runs before the app starts
 * Re-fetches user data if the user is logged in (has firebase_uid in localStorage)
 * Blocks app initialization until complete to prevent flicker and ensure guards have user data
 */
async function initializeUserData(): Promise<void> {
  const userStateService = inject(UserStateService);
  const loginService = inject(LoginService);
  const userService = inject(UserService);
  const userInteractionService = inject(UserInteractionService);
  const analyticsService = inject(AnalyticsService);

  // Initialize user interaction detection service
  userInteractionService.initialize();

  // Start PostHog (no-op unless environment.posthogKey is set). Not awaited:
  // the lazy-loaded posthog-js chunk shouldn't block app startup.
  void analyticsService.initialize();

  const firebaseUid = localStorage.getItem('firebase_uid');
  if (!firebaseUid) return;

  try {
    const response = await firstValueFrom(loginService.verifyToken());
    if (response.status !== 'success' || !response.user) {
      clearLocalStorage();
      return;
    }

    const uidToUse = firebaseUid || response.user.uid;

    try {
      const user = await firstValueFrom(userService.getUserData(uidToUse));
      userStateService.setUser(user);
    } catch (error) {
      console.error('Failed to fetch user data on init:', error);
      clearLocalStorage();
      return;
    }
  } catch (error) {
    console.error('Failed to verify token on init:', error);
    clearLocalStorage();
    return;
  }
}

/**
 * Helper function to clear all auth-related localStorage items
 */
function clearLocalStorage(): void {
  localStorage.removeItem('firebase_uid');
  localStorage.removeItem('email');
  localStorage.removeItem('role');
  localStorage.removeItem('is_name');
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(routes),
    provideHttpClient(
      withInterceptors([authInterceptor])
    ),
    provideFirebaseApp(() => initializeApp(environment.firebaseConfig)),
    provideAuth(() => getAuth()),
    provideAppInitializer(initializeUserData)
  ]
};
